import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { ShoppingCart, Menu, X, Sparkles, User, LogOut, Package } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import AuthDialog from "./AuthDialog";

const NAV = [
  { to: "/", label: "Inicio" },
  { to: "/personalizar", label: "Personalizar funda" },
  { to: "/accesorios", label: "Accesorios" },
  { to: "/emprende", label: "Emprende" },
  { to: "/contacto", label: "Contacto" },
];

export default function Header() {
  const { count, setOpen } = useCart();
  const { user, logout } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const [userMenu, setUserMenu] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const onWhatsApp = () => {
    window.open("https://wa.me/56975359774?text=" + encodeURIComponent("Hola, quiero personalizar una funda"), "_blank");
  };

  return (
    <header className="sticky top-0 z-50 glass border-b border-slate-200/60" data-testid="site-header">
      <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12">
        <div className="h-16 md:h-20 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2.5" data-testid="logo-link">
            <img
              src="/brand/torvi_icon.png"
              alt="Torvi Accesorios"
              className="h-10 md:h-12 w-auto select-none"
              draggable="false"
            />
            <div className="leading-none block">
  <div className="font-heading font-black text-sm md:text-xl tracking-tight text-black">
    TORVI
  </div>
  <div className="text-[9px] md:text-xs uppercase tracking-[0.25em] text-slate-500 font-bold">
    Accesorios
  </div>
</div>
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {NAV.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.to.replace("/", "") || "home"}`}
                className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${
                  location.pathname === n.to ? "bg-black text-white" : "text-slate-700 hover:bg-slate-100"
                }`}
              >
                {n.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            {user ? (
              <div className="relative">
                <button
                  onClick={() => setUserMenu((v) => !v)}
                  className="hidden sm:flex items-center gap-2 px-3 h-10 rounded-full bg-slate-100 hover:bg-slate-200 text-sm font-bold transition"
                  data-testid="user-menu-btn"
                >
                  <span className="w-7 h-7 rounded-full bg-[#0EA5E9] text-white flex items-center justify-center text-xs">
                    {user.nombre?.[0]?.toUpperCase() || "U"}
                  </span>
                  <span className="max-w-[120px] truncate">Hola, {user.nombre}</span>
                </button>
                {userMenu && (
                  <div className="absolute right-0 top-12 w-56 bg-white rounded-2xl shadow-xl border border-slate-200 p-2" data-testid="user-menu">
                    <div className="px-3 py-2 border-b border-slate-100">
                      <div className="font-bold text-sm">{user.nombre} {user.apellido}</div>
                      <div className="text-xs text-slate-500 truncate">{user.correo}</div>
                    </div>
                    <button onClick={() => { navigate("/mis-pedidos"); setUserMenu(false); }} className="w-full text-left px-3 py-2 rounded-lg hover:bg-slate-100 text-sm font-semibold inline-flex items-center gap-2" data-testid="user-orders-link">
                      <Package size={14} /> Mis pedidos
                    </button>
                    <button onClick={() => { logout(); setUserMenu(false); }} className="w-full text-left px-3 py-2 rounded-lg hover:bg-red-50 text-red-600 text-sm font-semibold inline-flex items-center gap-2" data-testid="user-logout-btn">
                      <LogOut size={14} /> Cerrar sesión
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <button
                onClick={() => setAuthOpen(true)}
                className="hidden sm:flex items-center gap-2 px-3 h-10 rounded-full bg-slate-100 hover:bg-slate-200 text-sm font-bold transition"
                data-testid="header-login-btn"
              >
                <User size={15} /> Ingresar
              </button>
            )}
            <button
              onClick={onWhatsApp}
              data-testid="header-whatsapp-btn"
              className="hidden sm:flex w-10 h-10 rounded-full bg-emerald-500 hover:bg-emerald-600 items-center justify-center text-white transition"
              aria-label="WhatsApp"
            >
              <i className="fa-brands fa-whatsapp text-lg" />
            </button>
            <button
              onClick={() => setOpen(true)}
              data-testid="header-cart-btn"
              className="relative w-10 h-10 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition"
              aria-label="Carrito"
            >
              <ShoppingCart size={18} />
              {count > 0 && (
                <span className="absolute -top-1.5 -right-1.5 bg-[#0EA5E9] text-white text-[10px] min-w-[20px] h-5 px-1 flex items-center justify-center rounded-full font-bold tech-glow">
                  {count}
                </span>
              )}
            </button>
            <button
              onClick={() => setMenuOpen((v) => !v)}
              className="lg:hidden w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center"
              data-testid="mobile-menu-toggle"
            >
              {menuOpen ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </div>
        {menuOpen && (
          <div className="lg:hidden pb-4 flex flex-col gap-1">
            {NAV.map((n) => (
              <button
                key={n.to}
                onClick={() => { navigate(n.to); setMenuOpen(false); }}
                data-testid={`mobile-nav-${n.to.replace("/", "") || "home"}`}
                className="text-left px-4 py-3 rounded-xl bg-slate-50 hover:bg-slate-100 font-semibold"
              >
                {n.label}
              </button>
            ))}
            {user ? (
              <>
                <button onClick={() => { navigate("/mis-pedidos"); setMenuOpen(false); }} className="text-left px-4 py-3 rounded-xl bg-slate-50 hover:bg-slate-100 font-semibold inline-flex items-center gap-2">
                  <Package size={15} /> Mis pedidos
                </button>
                <button onClick={() => { logout(); setMenuOpen(false); }} className="text-left px-4 py-3 rounded-xl bg-red-50 text-red-600 font-semibold inline-flex items-center gap-2">
                  <LogOut size={15} /> Cerrar sesión
                </button>
              </>
            ) : (
              <button onClick={() => { setAuthOpen(true); setMenuOpen(false); }} className="text-left px-4 py-3 rounded-xl bg-slate-50 hover:bg-slate-100 font-semibold inline-flex items-center gap-2">
                <User size={15} /> Ingresar / Registrarse
              </button>
            )}
          </div>
        )}
      </div>
      <AuthDialog open={authOpen} onClose={() => setAuthOpen(false)} />
    </header>
  );
}
